import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Paper, Button, Typography, Stack, Chip, CircularProgress, Drawer } from '@mui/material'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { search as searchEsign } from 'store/academic/parent/esign'
import PageHeader from 'components/AppBar/PageHeader'
import NoRecords from 'components/NoRecords'
import { BasePathProvider } from 'components/Providers'
import Form from './Form'

const baseUrl = '/academic/esign'

function DocumentRow({ doc, signed, onOpen }) {
  const { t } = useTranslation()
  const response = doc.formResponses?.[0]

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        transition: 'border-color .2s, box-shadow .2s',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <ArticleOutlinedIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />

      <Box sx={{ flex: 1, minWidth: 160 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {t(doc.title) || doc.description}
        </Typography>
        {signed && (
          <Typography variant="caption" color="text.secondary">
            {t('Signed by')} {response?.signature || '—'}
            {response?.signatureTime ? ` · ${dayjs(response.signatureTime).format('MMM D, YYYY')}` : ''}
          </Typography>
        )}
      </Box>

      <Chip
        size="small"
        label={signed ? t('Signed') : t('Pending')}
        color={signed ? 'success' : 'warning'}
        variant={signed ? 'filled' : 'outlined'}
        sx={{ flexShrink: 0 }}
      />

      <Button
        variant={signed ? 'text' : 'contained'}
        size="small"
        startIcon={signed ? null : <HistoryEduOutlinedIcon />}
        onClick={() => onOpen(doc)}
        sx={{ flexShrink: 0 }}
      >
        {signed ? t('View') : t('View & Sign')}
      </Button>
    </Paper>
  )
}

export default function EsignList() {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const activeLanguage = (i18n.language || 'en').split('-')[0]

  const selectedStudent = useSelector(state => state.academic?.parentStudent?.selectedStudent)
  const memberId = selectedStudent?.personAccessViews?.[0]?.id
  const studentName = selectedStudent
    ? `${selectedStudent.firstName ?? ''} ${selectedStudent.lastName ?? ''}`.trim()
    : ''
  const esign = useSelector(state => state.academic?.parentStudent?.esign?.list)

  const [activeDocument, setActiveDocument] = useState(null)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!memberId) {
      setLoading(false)
      return
    }
    setLoading(true)
    dispatch(searchEsign({ memberId, activeLanguage })).finally(() => setLoading(false))
  }, [dispatch, memberId, activeLanguage])

  const pendingDocuments = esign?.filter(doc => !doc.formResponses || doc.formResponses.length === 0) || []
  const signedDocuments = esign?.filter(doc => doc.formResponses && doc.formResponses.length > 0) || []

  const handleOpen = doc => {
    setActiveDocument(doc)
    setOpen(true)
    navigate(`${baseUrl}/${doc.id}`)
  }

  const handleCloseDrawer = () => {
    setOpen(false)
    navigate(baseUrl)
  }

  return (
    <BasePathProvider value={baseUrl}>
      <Box sx={{ width: '100%', mx: 'auto' }}>
        <Box>
          <PageHeader title="E-Sign" />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {t('Pending Documents')}
                </Typography>
                <Chip size="small" label={pendingDocuments.length} sx={{ height: 20 }} />
              </Stack>
              {pendingDocuments.length ? (
                <Stack spacing={1.5}>
                  {pendingDocuments.map(doc => (
                    <DocumentRow key={doc.id} doc={doc} onOpen={handleOpen} />
                  ))}
                </Stack>
              ) : (
                <NoRecords message={t("You're all caught up — no documents to sign.")} />
              )}
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {t('Signed Documents')}
                </Typography>
                <Chip size="small" label={signedDocuments.length} sx={{ height: 20 }} />
              </Stack>
              {signedDocuments.length ? (
                <Stack spacing={1.5}>
                  {signedDocuments.map(doc => (
                    <DocumentRow key={doc.id} doc={doc} signed onOpen={handleOpen} />
                  ))}
                </Stack>
              ) : (
                <NoRecords message={t('No signed documents yet.')} />
              )}
            </Paper>
          </Stack>
        )}

        <Drawer
          anchor="right"
          PaperProps={{ sx: { width: { xs: '100%', sm: 600, md: 960 } } }}
          open={open}
          onClose={handleCloseDrawer}
        >
          {activeDocument && (
            <Form
              activeDocument={activeDocument}
              studentName={studentName}
              memberId={memberId}
              handleCloseDrawer={handleCloseDrawer}
              activeLanguage={activeLanguage}
            />
          )}
        </Drawer>
      </Box>
    </BasePathProvider>
  )
}
